import React from 'react'
import moment from 'moment'
import { formatNumber } from '../functions'
import logo from '../../assets/img/logo-new.svg'
const PayslipPDFContent = (company, payslip, imageBlob = null) => {
  const parseJson = (data) => {
    try {
      const parsed = JSON.parse(data || '[]')
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  const additionalAllowances = parseJson(payslip.additional_allowances).filter(
    (item) => item.amount > 0
  )
  const bonusBreakdown = parseJson(payslip.bonus_breakdown).filter(
    (item) => item.amount > 0
  )
  const otherDeductionsBreakdown = parseJson(
    payslip.other_deductions_breakdown
  ).filter((item) => item.amount > 0)
  const reimbursementBreakdown = parseJson(
    payslip.reimbursement_breakdown
  ).filter((item) => item.amount > 0)

  const totalOtherDeductions = otherDeductionsBreakdown.reduce(
    (sum, item) => sum + item.amount,
    0
  )

  const totalDeductions =
    Number(payslip.tax || 0) +
    Number(payslip.pension || 0) +
    Number(payslip.salary_advance || 0) +
    totalOtherDeductions

  return (
    <div className='w-full min-h-screen bg-white p-7'>
      <div className='max-w-7xl mx-auto space-y-4'>
        {/* Header Section */}
        <div className='flex justify-between items-start'>
          {/* Company Info */}
          <div className='space-y-4'>
            <div className='flex font-bold justify-center items-center'>
              {imageBlob ? (
                <img
                  src={imageBlob}
                  alt='company_logo'
                  className='w-[170px] h-[60px]'
                />
              ) : (
                <div className='bg-hr-primary-1 h-14 w-14 rounded-full flex justify-center items-center'>
                  <span className=' text-white text-2xl font-bold pb-4 flex'>
                    {company?.company_name?.charAt(0)}
                  </span>
                </div>
              )}
            </div>
            <div>
              <p className='text-lg text-hr-primary-1 font-semibold'>
                {company?.company_name}
              </p>
              <p className='text-base text-hr-primary-1 font-normal'>
                {company?.address}, {company?.country}
              </p>
              <p className='text-base text-hr-primary-1 font-medium'>
                {company?.email} {company?.email ? '|' : null} {company?.mobile}
              </p>
            </div>
          </div>

          {/* Payslip Info */}
          <div className='flex flex-col items-end justify-end'>
            <div className='flex items-center gap-1'>
              <p className='text-sm font-medium text-hr-primary-1'>
                Pay Month:
              </p>
              <p className='text-sm font-normal text-hr-primary-1'>
                {moment(payslip.month).format('MMMM, YYYY')}
              </p>
            </div>
            <div className='flex items-center gap-1'>
              <p className='text-sm font-medium text-hr-primary-1'>
                Date of Payment:
              </p>
              <p className='text-sm font-normal text-hr-primary-1'>
                {moment(payslip.payment_date).format('MMMM DD, YYYY')}
              </p>
            </div>
            <div className='flex items-center gap-1'>
              {payslip.staff_number && (
                <p className='text-sm font-medium text-hr-primary-1'>
                  Staff Number:
                </p>
              )}
              {payslip.staff_number && (
                <p className='text-sm font-normal text-hr-primary-1'>
                  {payslip.staff_number || '-'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Employee Info */}
        <div className='flex justify-between items-start bg-hr-primary-1 rounded p-[10px]'>
          <div className='space-y-2 pb-[10px]'>
            <h2 className='text-lg font-semibold text-white'>
              {payslip.employee_first_name} {payslip.employee_last_name}
            </h2>
            <p className='text-sm font-medium text-white'>
              {payslip.employee_email}
            </p>
            <p className='text-sm font-medium text-white'>
              {payslip.employee_phone}
            </p>
          </div>
          <div className='space-y-2 text-right pb-2'>
            <h2 className='text-lg font-semibold text-white'>
              {payslip.bank_name}
            </h2>
            <p className='text-sm font-medium text-white'>
              {payslip.account_number}
            </p>
            <p className='text-sm font-medium text-white'>
              {payslip.account_name}
            </p>
          </div>
        </div>

        {/* Earnings Section */}
        <div className='space-y-3'>
          <h3 className='text-xl font-bold text-hr-primary-1'>EARNINGS</h3>
          <div>
            <div className='px-6 h-12 pb-2 bg-[#001984]/10 flex flex-row justify-between items-center w-full'>
              <span className='flex h-fit text-hr-primary-1 text-base font-semibold pb-2'>
                Type
              </span>
              <span className='flex h-fit text-right text-hr-primary-1 text-base font-semibold pb-2'>
                Amount
              </span>
            </div>

            <div className='px-6 py-3'>
              <div className='flex justify-between'>
                <div className='space-y-3'>
                  {payslip.basic_salary > 0 && (
                    <p className='text-sm font-normal'>Basic Salary</p>
                  )}
                  {payslip.housing_allowance > 0 && (
                    <p className='text-sm font-normal'>Housing Allowance</p>
                  )}
                  {payslip.transport_allowance > 0 && (
                    <p className='text-sm font-normal'>Transport Allowance</p>
                  )}
                  {payslip.leave_allowance > 0 && (
                    <p className='text-sm font-normal'>Leave Allowance</p>
                  )}
                  {payslip.utility_allowance > 0 && (
                    <p className='text-sm font-normal'>Utility Allowance</p>
                  )}
                  {payslip.lunch_allowance > 0 && (
                    <p className='text-sm font-normal'>Lunch Allowance</p>
                  )}
                  {payslip.overtime > 0 && (
                    <p className='text-sm font-normal'>Overtime</p>
                  )}
                  {payslip.other_allowance > 0 && (
                    <p className='text-sm font-normal'>Other Allowance</p>
                  )}
                  {payslip.benefit_in_kind > 0 && (
                    <p className='text-sm font-normal'>Benefit in Kind</p>
                  )}
                  {additionalAllowances.length > 0 &&
                    additionalAllowances.map((item, index) => (
                      <p key={index} className='text-sm font-normal'>
                        {item.title}
                      </p>
                    ))}

                  {bonusBreakdown.length > 0 &&
                    bonusBreakdown.map((item, index) => (
                      <p key={index} className='text-sm font-normal'>
                        {item.description}
                      </p>
                    ))}

                  {reimbursementBreakdown?.length > 0 &&
                    reimbursementBreakdown?.map((item, index) => (
                      <p key={index} className='text-sm font-normal'>
                        {item.description}
                      </p>
                    ))}
                  <p className='text-sm font-bold'>Gross Pay</p>
                </div>
                <div className='space-y-3 text-right'>
                  {payslip.basic_salary > 0 && (
                    <p className='text-sm font-normal'>
                      {formatNumber(payslip.basic_salary)}
                    </p>
                  )}
                  {payslip.housing_allowance > 0 && (
                    <p className='text-sm font-normal'>
                      {formatNumber(payslip.housing_allowance)}
                    </p>
                  )}
                  {payslip.transport_allowance > 0 && (
                    <p className='text-sm font-normal'>
                      {formatNumber(payslip.transport_allowance)}
                    </p>
                  )}
                  {payslip.leave_allowance > 0 && (
                    <p className='text-sm font-normal'>
                      {formatNumber(payslip.leave_allowance)}
                    </p>
                  )}
                  {payslip.utility_allowance > 0 && (
                    <p className='text-sm font-normal'>
                      {formatNumber(payslip.utility_allowance)}
                    </p>
                  )}
                  {payslip.lunch_allowance > 0 && (
                    <p className='text-sm font-normal'>
                      {formatNumber(payslip.lunch_allowance)}
                    </p>
                  )}
                  {payslip.overtime > 0 && (
                    <p className='text-sm font-normal'>
                      {formatNumber(payslip.overtime)}
                    </p>
                  )}
                  {payslip.other_allowance > 0 && (
                    <p className='text-sm font-normal'>
                      {formatNumber(payslip.other_allowance)}
                    </p>
                  )}
                  {payslip.benefit_in_kind > 0 && (
                    <p className='text-sm font-normal'>
                      {formatNumber(payslip.benefit_in_kind)}
                    </p>
                  )}
                  {additionalAllowances?.length > 0 &&
                    additionalAllowances?.map((item, index) => (
                      <p key={index} className='text-sm font-normal'>
                        {formatNumber(item.amount)}
                      </p>
                    ))}

                  {bonusBreakdown?.length > 0 &&
                    bonusBreakdown?.map((item, index) => (
                      <p key={index} className='text-sm font-normal'>
                        {formatNumber(item.amount)}
                      </p>
                    ))}

                  {reimbursementBreakdown?.length > 0 &&
                    reimbursementBreakdown?.map((item, index) => (
                      <p key={index} className='text-sm font-normal'>
                        {formatNumber(item.amount)}
                      </p>
                    ))}
                  <p className='text-sm font-bold'>
                    {formatNumber(payslip.gross_pay)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Deductions Section */}
        <div className='space-y-4'>
          <h3 className='text-xl font-bold text-hr-primary-1'>DEDUCTIONS</h3>
          <div>
            <div className='px-6 h-12 pb-2 bg-[#001984]/10 flex flex-row justify-between items-center w-full'>
              <span className='flex h-fit text-hr-primary-1 text-base font-semibold pb-2'>
                Type
              </span>
              <span className='flex h-fit text-right text-hr-primary-1 text-base font-semibold pb-2'>
                Amount
              </span>
            </div>

            <div className='px-6 py-3'>
              <div className='flex justify-between'>
                <div className='space-y-3'>
                  <p className='text-sm font-normal'>PAYE Tax</p>
                  <p className='text-sm font-normal'>Pension</p>
                  {payslip.salary_advance > 0 && (
                    <p className='text-sm font-bold'>Salary Advance</p>
                  )}
                  {payslip.employee_voluntary_pension > 0 && (
                    <p className='text-sm font-bold'>
                      Employee Voluntary Pension
                    </p>
                  )}
                  {payslip.employer_voluntary_pension > 0 && (
                    <p className='text-sm font-bold'>
                      Employer Voluntary Pension
                    </p>
                  )}

                  {otherDeductionsBreakdown.length > 0 &&
                    otherDeductionsBreakdown.map((item, index) => (
                      <p key={index} className='text-sm font-normal'>
                        {item.description}
                      </p>
                    ))}
                  <p className='text-sm font-bold'>Total Deductions</p>
                </div>
                <div className='space-y-3 text-right'>
                  <p className='text-sm font-normal'>
                    {formatNumber(payslip.tax)}
                  </p>
                  <p className='text-sm font-normal'>
                    {formatNumber(payslip.pension)}
                  </p>
                  {payslip.salary_advance > 0 && (
                    <p className='text-sm font-normal'>
                      {formatNumber(payslip.salary_advance)}
                    </p>
                  )}
                  {payslip.employee_voluntary_pension > 0 && (
                    <p className='text-sm font-normal'>
                      {formatNumber(payslip.employee_voluntary_pension)}
                    </p>
                  )}
                  {payslip.employer_voluntary_pension > 0 && (
                    <p className='text-sm font-normal'>
                      {formatNumber(payslip.employer_voluntary_pension)}
                    </p>
                  )}
                  {otherDeductionsBreakdown?.length > 0 &&
                    otherDeductionsBreakdown?.map((item, index) => (
                      <p key={index} className='text-sm font-normal'>
                        {formatNumber(item.amount)}
                      </p>
                    ))}

                  <p className='text-sm font-bold'>
                    {formatNumber(totalDeductions)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Net Pay Section */}
        <div className='px-6 h-12 pb-2 bg-[#001984]/10 flex flex-row justify-between items-center w-full'>
          <p className='text-hr-primary-1 text-base font-bold'>Net Pay</p>
          <p className='text-hr-primary-1 text-lg font-bold'>
            {formatNumber(payslip.net_pay)}
          </p>
        </div>

        {/* Add android and ios button  */}
        <div className='flex justify-center items-center space-x-4 mb-2'>
          <a
            href='https://play.google.com/store/apps/details?id=com.centelhr.app'
            target='_blank'
            rel='noreferrer'
          >
            <img
              src='/playstore.png'
              alt='Google Play Badge'
              className='h-12'
            />
          </a>
          <a
            href='https://apps.apple.com/ng/app/centelhr/id6743995408'
            target='_blank'
            rel='noreferrer'
          >
            <img src='/appstore.png' alt='App Store Badge' className='h-12' />
          </a>
        </div>

        {/* Footer */}
        <a
          href='https://centelhr.com'
          target='_blank'
          rel='noreferrer'
          className='text-sm text-center text-hr-primary-1 font-normal'
        >
          <div className='text-center space-y-2'>
            <p className='text-sm font-normal'>Powered By</p>
            <img src={logo} alt='centelHr Logo' className='h-8 mx-auto' />
          </div>
        </a>
      </div>
    </div>
  )
}

export default PayslipPDFContent
